// Purchasing (design/screens/purchasing.html): the page's tabs — Purchase orders, Lab orders and Bills are Sprint 7's, each saying
// so and sending nothing; Goods receipts (UC-PURCHASING-008) is a stock role's first tab, with Receive delivery in the page head
// (UC-PURCHASING-005); a role without `purchasing.po.read` sees Suppliers only and is sent there (BR-PURCHASING-011) — and
// Purchasing › Suppliers (UC-PURCHASING-003): the organisation's suppliers by name, 50 a page,
// with their types, contact, order channel, lead time and the acting branch's account; the search (the start of any word of the
// name, from one character), type and status kept in the address; New supplier for an Owner or Admin. The prototype draws no
// supplier list: it is designed from the use case in the prototype's vocabulary.
import { ORDER_CHANNEL_LABELS, SUPPLIER_STATUS_FILTERS, SUPPLIER_TYPES, type SupplierStatusFilter, type SupplierType } from "@visionx/contracts";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { suppliers } from "../api/endpoints.purchasing";
import { isApiError } from "../api/problem";
import { can, useSessionView } from "../session/store";
import { useOneStore } from "../session/one-store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Empty, ErrorAlert, Loading, Pager, Panel, Select, StatusChip } from "../ui/bits";
import { Icon } from "../ui/icons";
import { SUPPLIER_TYPE_LABEL, supplierTypesText } from "./CatalogueShared";
import { NoAccessPanel, PageHead } from "./common";
import { NotHereSection } from "./Placeholder";
import { useDebouncedValue } from "./PatientsShared";

const PAGE = 50;

interface PurchasingTab { key: string; label: string; to: string; needsOrders: boolean; later?: string }
/** The tabs in the prototype's order, with Suppliers after them; the ones built by a later stage say when (BR-TENANCY-032). */
export const PURCHASING_TABS: readonly PurchasingTab[] = [
  { key: "orders", label: "Purchase orders", to: "/purchasing/orders", needsOrders: true, later: "Purchase orders are not built yet. Planned for Sprint 7." },
  { key: "lab-orders", label: "Lab orders", to: "/purchasing/lab-orders", needsOrders: true, later: "Lab orders are not built yet. Planned for Sprint 7." },
  { key: "receipts", label: "Goods receipts", to: "/purchasing/receipts", needsOrders: true },
  { key: "bills", label: "Bills", to: "/purchasing/bills", needsOrders: true, later: "Bills are not built yet. Planned for Sprint 7." },
  { key: "suppliers", label: "Suppliers", to: "/purchasing/suppliers", needsOrders: false },
];

function branchNameOf(s: ReturnType<typeof useSessionView>): string | null {
  return s?.switchableBranches.find((b) => b.id === s.activeBranchId)?.name ?? s?.assignments.find((a) => a.branchId === s.activeBranchId)?.branchName ?? null;
}

export function PurchasingTabs() {
  const s = useSessionView();
  const loc = useLocation();
  const tabs = PURCHASING_TABS.filter((t) => !t.needsOrders || can(s, "purchasing.po.read"));
  return (
    <div className="tabs">
      {tabs.map((t) => <NavLink key={t.key} to={t.to} className={loc.pathname.startsWith(t.to) ? "active" : ""}>{t.label}</NavLink>)}
    </div>
  );
}

/**
 * The page head: Receive delivery for the roles that post receipts (`purchasing.po.write`, UC-PURCHASING-005 E10) on every tab but
 * Suppliers, whose head is the supplier master's; New purchase order waits for Sprint 7.
 */
export function PurchasingHead({ actions, receive = true }: { actions?: ReactNode; receive?: boolean }) {
  const s = useSessionView();
  const block = useWriteBlock();
  const branch = branchNameOf(s);
  const canReceive = receive && can(s, "purchasing.po.write");
  return (
    <PageHead title="Purchasing" sub={`Purchase orders, lab orders, goods receipts, bills and suppliers${branch ? ` · ${branch}` : ""}`} actions={(actions || canReceive) && <>
      {actions}
      {canReceive && (block === null
        ? <Link className="btn primary" to="/purchasing/receipts/new"><Icon name="stock" />Receive delivery</Link>
        : <button className="btn primary" type="button" disabled title={WRITE_BLOCK_TITLE[block]}><Icon name="stock" />Receive delivery</button>)}
    </>} />
  );
}

/** /purchasing: a stock role's Goods receipts, everyone else's Suppliers (BR-PURCHASING-011). */
export function PurchasingHome() {
  const s = useSessionView();
  return <Navigate to={can(s, "purchasing.po.read") ? "/purchasing/receipts" : "/purchasing/suppliers"} replace />;
}

/** /purchasing/{orders|lab-orders|receipts|bills}: what a later stage builds, said plainly; nothing is asked of the server. */
export function PurchasingLater() {
  const s = useSessionView();
  const { tab } = useParams();
  const item = PURCHASING_TABS.find((t) => t.key === tab && t.later);
  if (!item) return <NotHereSection />;
  return (
    <section className="screen" id="s-purchasing">
      <div className="content">
        <PurchasingHead />
        <PurchasingTabs />
        {item.needsOrders && !can(s, "purchasing.po.read")
          ? <NoAccessPanel page={item.label} permission="purchasing.po.read" />
          : <div className="panel"><div className="empty"><b>{item.later}</b><span>The sidebar and the tabs keep every part in its final place so the layout does not shift when it lands.</span></div></div>}
      </div>
    </section>
  );
}

const isType = (v: string | null): v is SupplierType => (SUPPLIER_TYPES as readonly string[]).includes(v ?? "");
const isStatus = (v: string | null): v is SupplierStatusFilter => (SUPPLIER_STATUS_FILTERS as readonly string[]).includes(v ?? "");
const STATUS_WORD: Readonly<Record<SupplierStatusFilter, string>> = { Active: "Active", Inactive: "Inactive", All: "All" };

/** /purchasing/suppliers (UC-PURCHASING-003). */
export function Suppliers() {
  const s = useSessionView();
  const navigate = useNavigate();
  const location = useLocation();
  const block = useWriteBlock();
  const online = block === null;
  const canWrite = can(s, "purchasing.supplier.write");
  const branch = branchNameOf(s);
  // A practice of one store: one list, its account the store's, and no word of other branches (UC-PURCHASING-003 A5; C13-2).
  const oneStore = useOneStore();
  const [sp, setSp] = useSearchParams();
  const type = isType(sp.get("type")) ? (sp.get("type") as SupplierType) : undefined;
  const status: SupplierStatusFilter = isStatus(sp.get("status")) ? (sp.get("status") as SupplierStatusFilter) : "Active";
  const [typed, setTyped] = useState(sp.get("q") ?? "");
  const q = useDebouncedValue(typed.trim(), 250);
  useEffect(() => {
    if ((sp.get("q") ?? "") === q) return;
    setSp((p) => { const n = new URLSearchParams(p); if (q) n.set("q", q); else n.delete("q"); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  useEffect(() => { setCursors([]); }, [q, type, status]);
  const list = useQuery({ queryKey: ["suppliers", "list", q, type ?? "", status, cursor ?? ""], queryFn: () => suppliers.list({ q: q || undefined, type, status }, { limit: PAGE, cursor }), placeholderData: keepPreviousData });
  const [notice, setNotice] = useState<string | null>(null);
  const state = location.state as { notice?: string } | null;
  useEffect(() => {
    if (!state?.notice) return;
    setNotice(state.notice);
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.notice]);
  const setParam = (k: string, v: string | null) => setSp((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const clear = () => { setTyped(""); setSp(new URLSearchParams(), { replace: true }); };
  const filtered = typed !== "" || type !== undefined || status !== "Active";
  const rows = list.data?.data.items ?? [];
  const denied = list.isError && isApiError(list.error) && list.error.code === "PERMISSION_DENIED";
  return (
    <section className="screen" id="s-suppliers">
      <div className="content">
        <PurchasingHead receive={false} actions={canWrite && <button className="btn primary" type="button" disabled={!online} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => navigate("/purchasing/suppliers/new")}><Icon name="plus" />New supplier</button>} />
        <PurchasingTabs />
        {notice && <Alert tone="ok" actions={<button className="btn sm" type="button" onClick={() => setNotice(null)}>Dismiss</button>}><b>{notice}</b></Alert>}
        <div className="toolbar">
          <div className="input w-search">
            <Icon name="search" />
            <input type="search" aria-label="Search suppliers" placeholder="Supplier's name…" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
          </div>
          <Select aria-label="Type" boxClass="w-sel" value={type ?? ""} onChange={(e) => setParam("type", e.target.value || null)}>
            <option value="">Type · All types</option>
            {SUPPLIER_TYPES.map((t) => <option key={t} value={t}>Type · {SUPPLIER_TYPE_LABEL[t]}</option>)}
          </Select>
          <Select aria-label="Status" boxClass="w-sel" value={status} onChange={(e) => setParam("status", e.target.value === "Active" ? null : e.target.value)}>
            {SUPPLIER_STATUS_FILTERS.map((st) => <option key={st} value={st}>Status · {STATUS_WORD[st]}</option>)}
          </Select>
          {filtered && <button className="btn sm" type="button" onClick={clear}>Clear filters</button>}
          <span className="right small faint">{oneStore && branch ? `The account is ${branch}'s` : <>One supplier list for every branch · the account is {branch ?? "the acting branch"}'s</>}</span>
        </div>
        {list.isPending && <Loading what="suppliers" />}
        {list.isError && (denied ? <NoAccessPanel page="Suppliers" error={list.error} /> : <ErrorAlert error={list.error} />)}
        {list.data && (
          <Panel tight foot={<>
            <span className="small faint">By name · inactive suppliers keep everything already linked to them</span>
            <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!list.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, list.data.data.nextCursor!])} />
          </>}>
            {rows.length === 0 ? (
              <Empty title={q ? `No supplier matches “${q}”.` : "No suppliers match these filters."} actions={filtered ? <button className="btn" type="button" onClick={clear}>Clear filters</button> : canWrite && online ? <Link className="btn primary" to="/purchasing/suppliers/new">New supplier</Link> : null}>
                {q ? "The search matches the start of a word of the name." : !filtered ? "An Owner or Admin adds the practice's suppliers." : ""}
              </Empty>
            ) : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>Supplier</th><th>Types</th><th>Contact</th><th>Orders by</th><th>Lead time</th><th>Account{branch ? ` (${branch})` : ""}</th><th>Status</th></tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="clickable" onClick={() => navigate(`/purchasing/suppliers/${r.id}`)}>
                        <td className="strong"><Link to={`/purchasing/suppliers/${r.id}`} onClick={(e) => e.stopPropagation()}>{r.name}</Link></td>
                        <td>{supplierTypesText(r.types)}</td>
                        <td>{r.contactName || r.phone ? <>{r.contactName ?? ""}{r.contactName && r.phone ? " · " : ""}{r.phone ? <span className="nw">{r.phone}</span> : null}</> : <span className="faint">—</span>}</td>
                        <td className="nw">{ORDER_CHANNEL_LABELS[r.orderChannel]}</td>
                        <td className="nw">{r.leadTimeDays === null ? <span className="faint">—</span> : `${r.leadTimeDays} working day${r.leadTimeDays === 1 ? "" : "s"}`}</td>
                        <td className="mono">{r.accountNumber ?? <span className="faint">—</span>}</td>
                        <td><StatusChip status={r.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
      </div>
    </section>
  );
}
